import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import { round, get, camelCase } from 'lodash';
import { withT, wrap } from '../../../lib/wrappers';
import numericInput from '../../../lib/numericInput';
import s from '../../../redux/selectors';
import a, { pick, bindActionCreators } from '../../../redux/actions';

const criticalAssumptionsContent = [
  {
    description: 'Price of gasoline:',
    currency: true,
    decimals: 2,
    input: {
      id: 'input_takeaction_assumption_price_of_gasoline',
      size: '4',
      maxlength: '4',
    },
    suffix: (t, isMetric) => (isMetric ? t('per L') : t('per gallon')),
  }, {
    description: 'Electricity price',
    suffix: t => t('cents/ kWh'),
    input: {
      id: 'input_takeaction_assumption_kwhprice',
      size: '4',
      maxlength: '4',
    },
  }, {
    description: 'Electricity emission factor',
    show: 'input_footprint_housing_gco2_per_kwh',
    suffix: t => t('gCO<sub>2</sub>/ kWh'),
  }, {
    description: 'Natural gas price',
    currency: true,
    suffix: (t, isMetric) => (isMetric ? `${t('per m')}<sup>3</sup>` : t('per therm')),
    input: {
      id: 'input_takeaction_assumption_ngprice',
      size: '4',
      maxlength: '4',
    },
  },
];

class CriticalAssumptions extends Component {

  get assumptions_content() {
    return criticalAssumptionsContent;
  }

  get t() {
    return this.props.t;
  }

  get locale() {
    return this.props.locale;
  }

  displayStateValue({ show, suffix, decimals, prefix }) {
    let state_id = show;
    if (state_id.includes('display_takeaction')) {
      state_id = state_id.replace(/display_takeaction/i, 'input_takeaction');
    }
    let value = this.userApiValue(state_id);
    if (decimals || decimals === 0) value = round(value, decimals);
    if (!isNaN(value)) value = numericInput.format(value, { autoDecimal: true });
    if (!suffix) {
      return `${prefix || ''}${value}`;
    }
    return `${prefix || ''}${value} ${suffix(this.t, this.props.isMetric)}`;
  }

  updateActionInput(event) {
    const action = this;
    const val = event.target.value;
    const id = event.target.id;
    const update = {};

    update[id] = val;
    update.input_changed = id;
    action.props.computeTakeActionResults({ [id]: val });
  }

  setInputState(id) {
    if (this.props[camelCase(id)]) {
      return this.props[camelCase(id)].ensuredDisplay;
    }
    return this.userApiValue(id);
  }

  userApiValue(apiKey) {
    const userFootprint = this.props.user_footprint;
    if (/input_/.test(apiKey)) {
      let value = get(userFootprint, `inputs.${apiKey}`);
      if (!value && value !== '') value = get(this.props.average_footprint, `get_defaults_and_results.${apiKey}`);
      return value;
    } else if (/result_takeaction/.test(apiKey)) {
      return get(userFootprint, `compute_takeaction_results.${apiKey}`);
    }
    return get(userFootprint, `compute_footprint.${apiKey}`);
  }

  render() {
    return (
      <table
        className="action__critical-assumptions action__assumptions"
      >
        <tbody>
          {
            this.assumptions_content.map((block, i) => (
              <tr key={`block-${i}`}>
                <td>
                  <p className="text-right">
                    {!block.text_url && <span >{this.t(block.description)}</span>}
                    {block.text_url && <a href={block.text_url}>{this.t(block.description)}</a>}
                  </p>
                </td>
                <td>
                  {block.input &&
                    <p className="text-left">
                      {block.currency && this.locale !== 'fr' && <span>$ </span>}
                      <input
                        aria-label={this.t(block.description)}
                        type="text"
                        id={block.input.id}
                        value={this.setInputState(block.input.id)}
                        size={block.input.size}
                        maxLength={block.input.maxlength}
                        onChange={e => this.updateActionInput(e)}
                      />
                      {block.currency && this.locale === 'fr' && <span> $</span>}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: `&nbsp;${block.suffix(this.t, this.props.isMetric)}`,
                        }}
                      />
                    </p>
                  }
                  {block.show &&
                    <p id={block.show} className="text-left">
                      <span>{this.t(block.prefix)} </span>
                      <span
                        dangerouslySetInnerHTML={{ __html: this.displayStateValue(block) }}
                      />
                    </p>
                  }
                </td>
              </tr>
            ))
          }
        </tbody>
      </table>
    );
  }
}

const mapStateToProps = (state, ownProps) => [
  'inputTakeactionAssumptionPriceOfGasoline',
  'inputTakeactionAssumptionNgprice',
  'inputTakeactionAssumptionKwhprice',
  'isMetric',
  'locale',
  'user_footprint',
  'average_footprint',
].reduce((props, prop) => ({
  ...props,
  [prop]: s[prop](state, ownProps),
}), {});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, ['computeTakeActionResults']),
  dispatch,
);

CriticalAssumptions.propTypes = {
  user_footprint: PropTypes.shape({}).isRequired,
  average_footprint: PropTypes.shape({}).isRequired,
  t: PropTypes.func.isRequired,
  isMetric: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

export default wrap(
  withT('takeAction'),
  connect(mapStateToProps, mapDispatchToProps),
  CriticalAssumptions,
);
